import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { FileText, Save, AlertTriangle, History, GitCompare, X, Download, FileArchive, Trash2 } from 'lucide-react';

const AdminLegalDocuments = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedType, setSelectedType] = useState('terms');
  const [editForm, setEditForm] = useState({
    title: '',
    content: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [placeholders, setPlaceholders] = useState({ documents: [], total: 0 });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [diff, setDiff] = useState(null);
  const [diffFrom, setDiffFrom] = useState('');
  const [diffTo, setDiffTo] = useState('');
  const [superseded, setSuperseded] = useState({ superseded: [], count: 0, can_remove: false });
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchPlaceholders();
  }, []);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchPlaceholders = async () => {
    try {
      const res = await axios.get(`${API}/admin/legal/placeholders`, authHeader());
      setPlaceholders(res.data);
    } catch (error) {
      /* non-blocking */
    }
  };

  const openHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/legal/${selectedType}/history`, authHeader());
      setHistory(res.data);
      setDiff(null);
      if (res.data.length >= 2) {
        setDiffFrom(res.data[1].version);
        setDiffTo(res.data[0].version);
      } else {
        setDiffFrom(res.data[0]?.version || '');
        setDiffTo(res.data[0]?.version || '');
      }
      setConfirmPurge(false);
      loadSuperseded();
      setShowHistory(true);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  const loadSuperseded = async () => {
    try {
      const res = await axios.get(`${API}/admin/legal/${selectedType}/superseded`, authHeader());
      setSuperseded(res.data);
    } catch (error) {
      setSuperseded({ superseded: [], count: 0, can_remove: false });
    }
  };

  const removeSuperseded = async () => {
    setPurging(true);
    try {
      const res = await axios.delete(`${API}/admin/legal/${selectedType}/superseded`, authHeader());
      toast.success(
        res.data.removed
          ? `Removed ${res.data.removed} pre-launch draft${res.data.removed === 1 ? '' : 's'}`
          : 'Nothing to remove'
      );
      setConfirmPurge(false);
      await Promise.all([openHistory(), fetchDocuments()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove those versions');
    } finally {
      setPurging(false);
    }
  };

  const supersededSet = new Set((superseded.superseded || []).map((s) => s.version));

  const loadDiff = async () => {    try {
      const res = await axios.get(
        `${API}/admin/legal/${selectedType}/diff?from=${diffFrom}&to=${diffTo}`,
        authHeader()
      );
      setDiff(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not compute diff');
    }
  };

  useEffect(() => {
    if (documents.length > 0) {
      loadDocument(selectedType);
    }
  }, [selectedType, documents]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/legal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load legal documents');
    } finally {
      setLoading(false);
    }
  };

  const loadDocument = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    if (doc) {
      setEditForm({
        title: doc.title,
        content: doc.content
      });
    }
  };

  /** Word files he can read away from the screen and mark up. One document, or all of them. */
  const downloadWord = async (documentType) => {
    const path = documentType
      ? `${API}/admin/legal/${documentType}/download`
      : `${API}/admin/legal/download-all`;
    try {
      const res = await axios.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentType
        ? `${documentType}.docx`
        : 'legal-documents.zip';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('That download could not be prepared');
    }
  };

  const handleSave = async (bumpVersion) => {
    if (bumpVersion && !window.confirm(
      'Publish this as a new version?\n\nEvery member this document applies to will be asked to '
      + 'review and agree before they can continue. Use "Save correction" instead for typos and '
      + 'wording changes.'
    )) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/legal`,
        {
          document_type: selectedType,
          title: editForm.title,
          content: editForm.content,
          bump_version: bumpVersion
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(
        res.data?.version_bumped
          ? `Published v${res.data.version} — members will be asked to re-agree.`
          : `Correction saved (still v${res.data?.version}) — nobody was re-prompted.`
      );
      fetchDocuments();
      fetchPlaceholders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const getDocumentLabel = (type) => {
    const labels = {
      'terms': 'Terms of Service',
      'privacy': 'Privacy Policy',
      'community_guidelines': 'Community Guidelines',
      'vendor_terms': 'Vendor Terms of Service',
      'marketplace_guidelines': 'Marketplace Guidelines',
      'practitioner_agreement': 'Practitioner / Expert / Contributor Agreement',
      'onboarding_checklist': 'Onboarding Agreement Checklist'
    };
    return labels[type] || type;
  };

  const DOC_TYPES = [
    'terms',
    'privacy',
    'community_guidelines',
    'vendor_terms',
    'marketplace_guidelines',
    'practitioner_agreement',
    'onboarding_checklist'
  ];

  if (loading) {
    return <div className="text-center py-20 text-white">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-3">Legal Documents Editor</h1>
        <p className="text-lg text-gray-300">Edit Terms of Service, Privacy Policy, and Community Guidelines</p>
      </div>

      {/* Sanity-check banner: unresolved [BRACKETED] placeholders */}
      {placeholders.total > 0 && (
        <Card className="border-2 border-amber-400 bg-amber-50" data-testid="legal-placeholder-banner">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 mb-1">
                  {placeholders.total} unresolved placeholder{placeholders.total === 1 ? '' : 's'} found
                </h3>
                <p className="text-sm text-amber-800 mb-2">
                  These bracketed values must be filled in before going live:
                </p>
                <ul className="space-y-1">
                  {placeholders.documents.map((d) => (
                    <li key={d.document_type} className="text-sm text-amber-900">
                      <button
                        onClick={() => setSelectedType(d.document_type)}
                        className="font-semibold underline hover:text-amber-700"
                        data-testid={`legal-placeholder-goto-${d.document_type}`}
                      >
                        {d.title}
                      </button>
                      : <span className="font-mono text-xs">{d.placeholders.join(', ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Selector */}
      <Card className="glass-effect border-cyan-500/30">
        <CardHeader>
          <CardTitle style={{ color: '#331a67', fontFamily: "'Cormorant Garamond', serif" }}>
            Select Document to Edit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {DOC_TYPES.map((type) => (
              <Button
                key={type}
                onClick={() => setSelectedType(type)}
                variant={selectedType === type ? 'default' : 'outline'}
                className={`flex items-center gap-2 ${selectedType === type ? 'bg-cyan-500' : ''}`}
                data-testid={`legal-doc-tab-${type}`}
              >
                <FileText className="w-4 h-4" />
                {getDocumentLabel(type)}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Editor */}
      <Card className="glass-effect border-cyan-500/30">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle style={{ color: '#331a67', fontFamily: "'Cormorant Garamond', serif" }}>
              Edit: {getDocumentLabel(selectedType)}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => downloadWord(selectedType)}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="legal-download-word-btn"
                title="Download this document as a Word file to read or mark up"
              >
                <Download className="w-4 h-4" />
                Word
              </Button>
              <Button
                onClick={() => downloadWord()}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="legal-download-all-btn"
                title="Download every legal document as Word files, zipped"
              >
                <FileArchive className="w-4 h-4" />
                All as Word
              </Button>
              <Button
                onClick={openHistory}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="legal-history-btn"
              >
                <History className="w-4 h-4" />
                Version History
              </Button>
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="legal-save-correction-btn"
                title="Fix typos and wording. Version and date stay as they are and no member is re-prompted."
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save correction'}
              </Button>
              <Button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="flex items-center gap-2"
                data-testid="legal-publish-version-btn"
                title="Publish a new version. Every member this applies to must agree before continuing."
              >
                <Save className="w-4 h-4" />
                Publish new version
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label style={{ color: '#000000' }}>Document Title</Label>
            <Input
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              className="bg-white border-cyan-500/30"
              style={{ color: '#000000' }}
              placeholder="e.g., Terms of Service"
            />
          </div>

          <div>
            <Label style={{ color: '#000000' }}>Document Content</Label>
            <Textarea
              value={editForm.content}
              onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
              className="bg-white border-cyan-500/30 font-mono min-h-[600px]"
              style={{ color: '#000000' }}
              placeholder="Enter document content here..."
            />
            <p className="text-xs mt-2" style={{ color: '#000000' }}>
              Tip: Use markdown formatting for headers (# Heading), lists (- item), etc.
            </p>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-700">
            <div className="text-sm" style={{ color: '#000000' }}>
              {documents.find(d => d.document_type === selectedType) && (
                <>
                  Version: {documents.find(d => d.document_type === selectedType).version} | 
                  Last Updated: {new Date(documents.find(d => d.document_type === selectedType).last_updated).toLocaleString()}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="legal-save-correction-btn-footer"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save correction'}
              </Button>
              <Button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="flex items-center gap-2"
                data-testid="legal-publish-version-btn-footer"
              >
                <Save className="w-4 h-4" />
                Publish new version
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card className="glass-effect border-cyan-500/30">
        <CardHeader>
          <CardTitle style={{ color: '#331a67', fontFamily: "'Cormorant Garamond', serif" }}>
            Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white p-6 rounded-lg max-h-96 overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#331a67' }}>{editForm.title}</h2>
            <div className="whitespace-pre-wrap" style={{ color: '#000000' }}>
              {editForm.content || 'Content will appear here...'}
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Version History + Diff modal */}
      {showHistory && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="legal-history-modal"
        >
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#331a67' }}>
                <History className="w-6 h-6" /> {getDocumentLabel(selectedType)} — History
              </h2>
              <button onClick={() => setShowHistory(false)} data-testid="legal-history-close">
                <X className="w-6 h-6 text-gray-500 hover:text-gray-800" />
              </button>
            </div>

            {history.length === 0 ? (
              <p className="text-gray-600">No saved versions yet.</p>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Saved versions:</p>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {history.map((h) => (
                      <li key={h.version}>
                        <span className="font-mono font-semibold">v{h.version}</span>
                        {h.last_updated && (
                          <span className="text-gray-500"> · {new Date(h.last_updated).toLocaleString()}</span>
                        )}
                        {supersededSet.has(h.version) && (
                          <span className="ml-2 text-xs font-semibold text-amber-700">pre-launch draft</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {superseded.count > 0 && (
                  <div
                    className="mb-4 p-4 rounded-lg border border-amber-300 bg-amber-50"
                    data-testid="legal-superseded-panel"
                  >
                    <p className="text-sm font-semibold text-amber-900">
                      {superseded.count} draft version{superseded.count === 1 ? '' : 's'} numbered above the
                      published v{superseded.count > 0 ? superseded.current_version : ''}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      v{superseded.superseded[0]?.version} – v
                      {superseded.superseded[superseded.superseded.length - 1]?.version}. These were drafted
                      before the finished documents were renumbered from 1.0, so they sort above the current
                      version as though they were newer. No member has accepted any of them — versions anyone
                      agreed to are never listed here.
                    </p>
                    {!superseded.can_remove ? (
                      <p className="text-xs text-amber-800 mt-2 italic">
                        Only the platform owner can remove version history.
                      </p>
                    ) : !confirmPurge ? (
                      <Button
                        onClick={() => setConfirmPurge(true)}
                        className="mt-3 bg-amber-700 hover:bg-amber-800 text-white"
                        data-testid="legal-remove-superseded-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Remove pre-launch drafts
                      </Button>
                    ) : (
                      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="legal-purge-confirm">
                        <span className="text-xs font-semibold text-amber-900">
                          Delete {superseded.count} draft{superseded.count === 1 ? '' : 's'} permanently?
                        </span>
                        <Button
                          onClick={removeSuperseded}
                          disabled={purging}
                          className="bg-red-700 hover:bg-red-800 text-white"
                          data-testid="legal-purge-confirm-btn"
                        >
                          {purging ? 'Removing…' : 'Yes, remove them'}
                        </Button>
                        <Button
                          onClick={() => setConfirmPurge(false)}
                          variant="outline"
                          data-testid="legal-purge-cancel-btn"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <Label className="text-xs" style={{ color: '#000' }}>Compare from</Label>
                    <Select value={diffFrom} onValueChange={setDiffFrom}>
                      <SelectTrigger className="w-28 bg-white" data-testid="legal-diff-from"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {history.map((h) => <SelectItem key={h.version} value={h.version}>v{h.version}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs" style={{ color: '#000' }}>to</Label>
                    <Select value={diffTo} onValueChange={setDiffTo}>
                      <SelectTrigger className="w-28 bg-white" data-testid="legal-diff-to"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {history.map((h) => <SelectItem key={h.version} value={h.version}>v{h.version}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={loadDiff} className="flex items-center gap-2" data-testid="legal-diff-btn">
                    <GitCompare className="w-4 h-4" /> View Diff
                  </Button>
                </div>

                {diff && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Diff v{diff.from_version} → v{diff.to_version}
                    </p>
                    {diff.diff.length === 0 ? (
                      <p className="text-sm text-gray-500">No textual differences.</p>
                    ) : (
                      <pre className="text-xs font-mono bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto max-h-72" data-testid="legal-diff-output">
                        {diff.diff.map((line, i) => {
                          let color = '#d1d5db';
                          if (line.startsWith('+') && !line.startsWith('+++')) color = '#4ade80';
                          else if (line.startsWith('-') && !line.startsWith('---')) color = '#f87171';
                          else if (line.startsWith('@@')) color = '#60a5fa';
                          return <div key={i} style={{ color }}>{line || ' '}</div>;
                        })}
                      </pre>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLegalDocuments;
